import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@london-life/ui/components/dialog';
import { Switch } from '@london-life/ui/components/switch';
import {
  CABIN_KEYS,
  airportsOf,
  bagCoverOf,
  fareOf,
  formatPence,
  hourInLondon,
  isPeakHour,
  routeNumber,
  type AirportContent,
  type CabinKey,
} from '@london-life/contracts';
import { ChopperTab, JetTab } from './private-tabs';

type Props = {
  open: boolean;
  onClose: () => void;
  airport: AirportContent;
  // the airport flown from
  from: { placeId: string; name: string } | null;
  // is a place open on the map (an airport whose place is hidden takes no flights)
  isOpen: (placeId: string) => boolean;
  // is the player there now (otherwise a cab takes them, included)
  atAirport: boolean;
  walletPence: number | null;
  sending: boolean;
  onBook: (to: string, cabin: CabinKey, covered: boolean) => void;
  // the map's places, where the chopper can land
  places: { id: string; name: string; icon: string; area: string }[];
  // off in my own jet or the VIP chopper
  onFly: (mode: 'jet' | 'chopper', to: string) => void;
  // the wallet changed here (a jet bought or sold)
  onWallet: () => void;
  // the terminal itself (the place's own card)
  onTerminal?: () => void;
};
type Tab = 'air' | 'jet' | 'chopper';
const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'air', label: 'Air', icon: '✈️' },
  { id: 'jet', label: 'My jet', icon: '🛩️' },
  { id: 'chopper', label: 'Chopper', icon: '🚁' },
];

// Departures, as Lagos Life's: where to (the game's other airports; cities not built yet "open soon"), the
// cabin (with what is short when a seat costs more than the wallet holds), cover for the bags, and the total.
// A player away from the airport is taken there by cab, included. The fare rises at peak hours.
export function FlightsSheet({
  open,
  onClose,
  airport,
  from,
  isOpen,
  atAirport,
  walletPence,
  sending,
  onBook,
  places,
  onFly,
  onWallet,
  onTerminal,
}: Props) {
  const [tab, setTab] = useState<Tab>('air');
  const [to, setTo] = useState<string | null>(null);
  const [cabin, setCabin] = useState<CabinKey>('economy');
  const [covered, setCovered] = useState(false);
  const hour = hourInLondon();
  const peak = isPeakHour(airport, hour);
  const stops = useMemo(
    () => airportsOf(airport).filter((stop) => stop.visible && isOpen(stop.placeId) && stop.placeId !== from?.placeId),
    [airport, isOpen, from?.placeId],
  );
  const soon = airport.destinations.filter((destination) => destination.visible);
  useEffect(() => {
    if (!open) return;
    setTab('air');
    setTo(stops[0]?.placeId ?? null);
    setCabin('economy');
    setCovered(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const chosen = stops.find((stop) => stop.placeId === to) ?? null;
  const cover = bagCoverOf(airport);
  const fare = chosen ? fareOf(airport, chosen, cabin, hour) : 0;
  const total = fare + (covered ? cover.pence : 0);
  const short = walletPence === null ? 0 : Math.max(0, total - walletPence);
  const shortFor = (key: CabinKey) =>
    chosen && walletPence !== null
      ? Math.max(0, fareOf(airport, chosen, key, hour) + (covered ? cover.pence : 0) - walletPence)
      : 0;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="city-card city-phone flights-sheet">
        <DialogTitle>🛫 Departures</DialogTitle>
        <DialogDescription>
          {airport.airline.name}
          {from ? ` from ${from.name}` : ''}
          {peak ? ' · peak hours, fares up' : ''}
        </DialogDescription>
        <div className="flights-tabs" role="tablist" aria-label="How to fly">
          {TABS.map((item) => (
            <button
              type="button"
              key={item.id}
              role="tab"
              aria-selected={tab === item.id}
              className={tab === item.id ? 'active' : ''}
              onClick={() => setTab(item.id)}
            >
              <span aria-hidden="true">{item.icon}</span> {item.label}
            </button>
          ))}
        </div>
        {tab !== 'air' && !from ? (
          <p className="neighbours-note">Head to an airport to fly from it.</p>
        ) : tab === 'jet' && from ? (
          <JetTab
            airport={airport}
            from={from}
            isOpen={isOpen}
            walletPence={walletPence}
            sending={sending}
            onFly={(place) => onFly('jet', place)}
            onWallet={onWallet}
          />
        ) : tab === 'chopper' && from ? (
          <ChopperTab
            airport={airport}
            from={from}
            places={places}
            walletPence={walletPence}
            sending={sending}
            onFly={(place) => onFly('chopper', place)}
          />
        ) : (
          <>
            <strong className="flights-step">Where to?</strong>
            <div className="flights-to" role="radiogroup" aria-label="Where to?">
              {stops.map((stop) => (
                <button
                  type="button"
                  key={stop.placeId}
                  role="radio"
                  aria-checked={to === stop.placeId}
                  className={to === stop.placeId ? 'active' : ''}
                  onClick={() => setTo(stop.placeId)}
                >
                  <span className="flights-code">{stop.code}</span>
                  <span className="flights-to-name">
                    <strong>{stop.name}</strong>
                    <small>
                      Boarding {airport.boardingSeconds}s · Flight {stop.flightSeconds}s
                    </small>
                  </span>
                  <em>from {formatPence(fareOf(airport, stop, 'economy', hour))}</em>
                </button>
              ))}
              {soon.map((destination) => (
                <button type="button" key={destination.id} role="radio" aria-checked={false} disabled>
                  <span className="flights-code">{destination.code}</span>
                  <span className="flights-to-name">
                    <strong>
                      {destination.icon} {destination.name}
                    </strong>
                    <small>{destination.airport} · opens soon</small>
                  </span>
                </button>
              ))}
            </div>
            {!stops.length && <p className="neighbours-note">No flights from here right now. Check back soon ✈️</p>}
            {chosen && (
              <>
                <strong className="flights-step">Cabin</strong>
                <div className="flights-cabins" role="radiogroup" aria-label="Cabin">
                  {CABIN_KEYS.map((key) => {
                    const option = airport.cabins[key];
                    const lacking = shortFor(key);
                    return (
                      <button
                        type="button"
                        key={key}
                        role="radio"
                        aria-checked={cabin === key}
                        aria-label={option.label}
                        className={cabin === key ? 'active' : ''}
                        onClick={() => setCabin(key)}
                      >
                        <span aria-hidden="true">{option.icon}</span>
                        <strong>{option.label}</strong>
                        <em>{formatPence(fareOf(airport, chosen, key, hour))}</em>
                        {lacking > 0 && <small className="flights-short">{formatPence(lacking)} short</small>}
                      </button>
                    );
                  })}
                </div>
                <p className="flights-perk">
                  {airport.cabins[cabin].perk} · {airport.cabins[cabin].seat}
                </p>
                <label className="flights-cover">
                  <span>
                    🧳 <strong>Bag cover +{formatPence(cover.pence)}</strong>
                    <small>Covers lost bags</small>
                  </span>
                  <Switch checked={covered} onCheckedChange={setCovered} aria-label="Bag cover" />
                </label>
                {!atAirport && from && <p className="flights-perk">🚕 A cab to {from.name} is included</p>}
              </>
            )}
          </>
        )}
        {tab === 'air' && chosen && from && (
          <div className="flights-total">
            <span>
              <small>
                {airport.cabins[cabin].label} · {routeNumber(airport, from.placeId, chosen.placeId)} · One way
              </small>
              <strong>{formatPence(total)}</strong>
            </span>
            <button
              type="button"
              className="city-card-go"
              disabled={sending || short > 0}
              onClick={() => onBook(chosen.placeId, cabin, covered)}
            >
              {short > 0 ? `${formatPence(short)} short` : sending ? 'Booking…' : 'Book flight'}
            </button>
          </div>
        )}
        {onTerminal && from && (
          <button type="button" className="flights-terminal" onClick={onTerminal}>
            🏢 Go to the terminal
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
